import { store } from "@/src/store";
import { isFeuComUaOffice, toOfficeUrlForApi } from "@/src/utils/accessControl/formatOfficeUrl";

/**
 * Resolves `office_url` for qarshisu.net document create payloads.
 * Only from explicit value or Admission Office task/applicant context — no other fallbacks.
 * Always sent as domain only (`feu.com.ua`).
 */
export const resolveOfficeUrlForDocCreate = (explicit?: string | null): string | null => {
  const fromExplicit = toOfficeUrlForApi(explicit);
  if (fromExplicit) return fromExplicit;

  const fromContext = toOfficeUrlForApi(store.getState().admissionOfficeDoc.officeUrl);
  if (fromContext) return fromContext;

  return null;
};

/**
 * Ukraine FEU admission letter variant: task/applicant `office_url` wins;
 * otherwise sidebar `selectedSiteUrl` (office picker).
 */
export const resolveIsFeuComUaOffice = (explicit?: string | null): boolean => {
  const fromDoc = resolveOfficeUrlForDocCreate(explicit);
  if (fromDoc != null) return isFeuComUaOffice(fromDoc);
  return isFeuComUaOffice(store.getState().accessControl.selectedSiteUrl);
};

/**
 * Resolves `task_id` for qarshisu.net document create payloads.
 * From explicit value in the payload or Admission Office form context (Redux).
 */
export const resolveTaskIdForDocCreate = (explicit?: string | number | null): string | null => {
  const fromExplicit = String(explicit ?? "").trim();
  if (fromExplicit) return fromExplicit;

  const fromContext = String(store.getState().admissionOfficeDoc.taskId ?? "").trim();
  if (fromContext) return fromContext;

  return null;
};

const parsePositiveId = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
};

/**
 * Resolves Student Id for qarshisu.net `user_id` on document create.
 * From explicit payload fields, Admission Office form context (Redux), or `#student_id` input.
 */
export const resolveUserIdForDocCreate = (explicit?: {
  user_id?: string | number | null;
  student_id?: string | number | null;
  applicant_id?: string | number | null;
}): string => {
  const fromExplicit =
    parsePositiveId(explicit?.user_id) ??
    parsePositiveId(explicit?.student_id) ??
    parsePositiveId(explicit?.applicant_id);
  if (fromExplicit != null) return String(fromExplicit);

  const fromContext = parsePositiveId(store.getState().admissionOfficeDoc.applicantId);
  if (fromContext != null) return String(fromContext);

  if (typeof document !== "undefined") {
    const studentInput = document.querySelector("input#student_id") as HTMLInputElement | null;
    const fromDom = parsePositiveId(studentInput?.value);
    if (fromDom != null) return String(fromDom);
  }

  return "";
};

/**
 * Adds `office_url` only when it came from the task/applicant.
 * Otherwise the field is omitted from the payload.
 */
export const withOfficeUrlForDocCreate = <T extends object>(data: T): T | (T & { office_url: string }) => {
  const record = data as T & { office_url?: string | null };
  const officeUrl = resolveOfficeUrlForDocCreate(record.office_url);
  if (officeUrl == null) return data;
  return { ...data, office_url: officeUrl };
};

/**
 * Adds `office_url`, `task_id` (when available) and always `user_id` (Student Id or empty string)
 * to document create payloads for qarshisu.net.
 */
export const withAdmissionDocCreateFields = <T extends object>(
  data: T,
): T & { office_url?: string; task_id?: string; user_id: string } => {
  const record = data as T & {
    office_url?: string | null;
    task_id?: string | number | null;
    user_id?: string | number | null;
    student_id?: string | number | null;
    applicant_id?: string | number | null;
  };
  const officeUrl = resolveOfficeUrlForDocCreate(record.office_url);
  const taskId = resolveTaskIdForDocCreate(record.task_id);
  const userId = resolveUserIdForDocCreate(record);

  return {
    ...data,
    ...(officeUrl != null ? { office_url: officeUrl } : {}),
    ...(taskId != null ? { task_id: taskId } : {}),
    user_id: userId,
  };
};
